import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const HeroSection = () => {
  return (
    <section className="relative min-h-screen pt-20 flex items-center overflow-hidden bg-white w-full max-w-full">
      {/* Background Image Layer */}
      <div className="absolute inset-0 z-0">
        <motion.div
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
          className="w-full h-full"
        >
          <Image
            src="/assets/images/homepage-banner.jpg"
            alt="VASTECH Premium Elevator"
            className="w-full h-full object-cover object-right md:object-center"
          />
        </motion.div>

        {/* Modern Minimalist Overlay - Fading from white on the left to transparent on the right */}
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/80 to-transparent md:via-white/40 z-10"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
        <div className="max-w-2xl">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="flex items-center space-x-3 text-[#91756A] mb-8"
          >
            <div className="w-12 h-[2px] bg-[#91756A]"></div>
            <span className="text-xs font-bold uppercase tracking-[0.4em]">Excellence Redefined</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 1 }}
            className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-bold text-[#4D4C4A] leading-[1.05] mb-10 tracking-tighter italic"
          >
            ELEVATING THE <br />
            <span className="text-[#91756A]">STANDARDS</span> <br />
            OF COMFORT
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="text-slate-600 text-lg md:text-xl leading-relaxed mb-12 max-w-lg font-medium"
          >
            Innovative HVAC systems, smart elevators, and advanced building technologies designed to improve comfort, efficiency, and performance.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 sm:gap-6"
          >
            <Link to="/contact-consultation" className="w-full sm:w-auto">
              <Button
                variant="default"
                className="w-full sm:w-auto bg-[#4D4C4A] hover:bg-[#3d3c3a] text-white px-6 md:px-10 py-5 md:py-8 rounded-none text-xs font-bold uppercase tracking-[0.15em] md:tracking-[0.3em] h-auto shadow-2xl shadow-[#4D4C4A]/20 transition-all duration-500 hover:scale-105"
              >
                Request Consultation
              </Button>
            </Link>
            <Link to="/project-portfolio" className="w-full sm:w-auto">
              <Button
                variant="outline"
                className="w-full sm:w-auto border-[#4D4C4A]/20 text-[#4D4C4A] hover:bg-white/50 backdrop-blur-sm px-6 md:px-10 py-5 md:py-8 rounded-none text-xs font-bold uppercase tracking-[0.15em] md:tracking-[0.3em] h-auto border-2 transition-all duration-500 hover:border-[#4D4C4A]"
              >
                View Our Projects
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Subtle Bottom Accent */}
      <motion.div
        initial={{ opacity: 0, width: 0 }}
        animate={{ opacity: 1, width: "100%" }}
        transition={{ delay: 1.5, duration: 2 }}
        className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-[#91756A] via-[#4D4C4A] to-transparent z-30"
      ></motion.div>

      {/* Floating Design Element - Minimalist Line */}
      <div className="absolute right-12 top-1/2 -translate-y-1/2 hidden lg:flex flex-col items-center space-y-8 z-20">
        <div className="w-px h-32 bg-gradient-to-b from-transparent via-[#4D4C4A]/30 to-transparent"></div>
        <span className="rotate-90 text-[10px] font-bold tracking-[0.5em] text-[#91756A] uppercase whitespace-nowrap">Premium Solutions</span>
        <div className="w-px h-32 bg-gradient-to-b from-transparent via-[#4D4C4A]/30 to-transparent"></div>
      </div>
    </section>
  );
};

export default HeroSection;